import { DOMHelper } from "./dom-helper";
import { NEXT_STEP, OnboardingConstants, PREV_STEP } from "../constants/onboarding-constants";
export class Wizard {
    constructor(selector, activeStepClass, dotsSelector, activeDotClassName, isFullScreen = false, isMobile = false, nextStepEvent = NEXT_STEP, prevStepEvent = PREV_STEP) {
        this.currentStepIndex = 0;
        this.stepsSelector = selector;
        this.activeStepClassName = activeStepClass;
        this.dotsSelector = dotsSelector;
        this.activeDotClassName = activeDotClassName;
        this.isFullScreen = isFullScreen;
        this.steps = DOMHelper.querySelectorAsArray(this.stepsSelector);
        this.dots = DOMHelper.querySelectorAsArray(this.dotsSelector);
        this.prevStepEvent = prevStepEvent;
        this.nextStepEvent = nextStepEvent;
        this.isMobile = isMobile;
    }
    init() {
        this.steps = this.sortElementsByIndex();
        this.setFirstStep();
        this.setFirstDot();
        this.bindEvents();
        this.setFocusTrap();
    }
    setFirstStep() {
        const [firstStep] = this.steps;
        this.steps.forEach(step => {
            step.setAttribute("inert", "");
            step.setAttribute("aria-hidden", "true");
        });
        if (firstStep) {
            firstStep.classList.add(this.activeStepClassName);
            firstStep.removeAttribute("inert");
            firstStep.removeAttribute("aria-hidden");
            this.currentStepElement = firstStep;
        }
    }
    setFirstDot() {
        const [firstDot] = this.dots;
        firstDot === null || firstDot === void 0 ? void 0 : firstDot.classList.add(this.activeDotClassName);
        this.currentDotElement = firstDot;
    }
    bindEvents() {
        window.addEventListener(this.nextStepEvent, () => { this.nextStep(); });
        window.addEventListener(this.prevStepEvent, () => { this.previousStep(); });
    }
    nextStep() {
        if (this.currentStepIndex === this.steps.length - 1)
            return;
        this.toggleActiveClass();
        this.currentStepIndex += 1;
        this.currentStepElement = this.steps[this.currentStepIndex];
        this.currentDotElement = this.dots[this.currentStepIndex];
        this.toggleActiveClass();
        this.displayCurrentStepNumber();
        this.setFocusTrap();
    }
    previousStep() {
        if (this.currentStepIndex === 0)
            return;
        this.toggleActiveClass();
        this.currentStepIndex -= 1;
        this.currentStepElement = this.steps[this.currentStepIndex];
        this.currentDotElement = this.dots[this.currentStepIndex];
        this.toggleActiveClass();
        this.displayCurrentStepNumber();
        this.setFocusTrap();
    }
    toggleActiveClass() {
        var _a;
        this.steps.forEach((step, i) => {
            if (i === this.currentStepIndex) {
                step.classList.add(this.activeStepClassName);
                step.removeAttribute("inert");
                step.removeAttribute("aria-hidden");
            }
            else {
                step.classList.remove(this.activeStepClassName);
                step.setAttribute("inert", "");
                step.setAttribute("aria-hidden", "true");
            }
        });
        (_a = this.currentDotElement) === null || _a === void 0 ? void 0 : _a.classList.toggle(this.activeDotClassName);
    }
    sortElementsByIndex() {
        var _a;
        const elementsArray = [...this.steps];
        elementsArray.sort((a, b) => {
            const indexA = Number(a.dataset.stepIndex);
            const indexB = Number(b.dataset.stepIndex);
            return indexA - indexB;
        });
        const parentElement = (_a = elementsArray[this.currentStepIndex]) === null || _a === void 0 ? void 0 : _a.parentNode;
        elementsArray.forEach(element => parentElement === null || parentElement === void 0 ? void 0 : parentElement.appendChild(element));
        return elementsArray;
    }
    setFocusTrap() {
        if (this.isMobile)
            return;
        if (this.focusTrapHandler) {
            document.removeEventListener("keydown", this.focusTrapHandler);
        }
        const focusableSelectors = [
            "a[href]",
            "button:not([disabled])",
            "textarea:not([disabled])",
            "input:not([disabled]):not([type=hidden])",
            "select:not([disabled])",
            "[tabindex]:not([tabindex='-1'])",
            OnboardingConstants.focusTrapSelector
        ];
        this.focusTrapHandler = (e) => {
            if (e.key !== "Tab")
                return;
            let focusableElements = Array.from(this.currentStepElement.querySelectorAll(focusableSelectors.join(",")));
            focusableElements = focusableElements.filter(el => this.isElementVisible(el));
            const closeBtn = document.getElementById("closeDDBox");
            if (closeBtn && this.isElementVisible(closeBtn) && !focusableElements.includes(closeBtn)) {
                focusableElements.push(closeBtn);
            }
            if (focusableElements.length === 0) {
                this.currentStepElement.setAttribute("tabindex", "-1");
                this.currentStepElement.focus();
                return;
            }
            focusableElements = focusableElements.sort((a, b) => {
                if (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_PRECEDING)
                    return 1;
                return -1;
            });
            const firstEl = focusableElements[0];
            const lastEl = focusableElements[focusableElements.length - 1];
            if (e.shiftKey) {
                if (document.activeElement === firstEl) {
                    e.preventDefault();
                    lastEl.focus();
                }
            }
            else {
                if (document.activeElement === lastEl) {
                    e.preventDefault();
                    firstEl.focus();
                }
            }
        };
        document.addEventListener("keydown", this.focusTrapHandler);
        setTimeout(() => {
            const firstVisible = Array.from(this.currentStepElement.querySelectorAll(focusableSelectors.join(","))).find(el => this.isElementVisible(el)) || this.currentStepElement;
            const focusTrap = this.currentStepElement.querySelector(OnboardingConstants.focusTrapSelector);
            if (this.currentStepIndex === 0 && focusTrap) {
                focusTrap.focus();
                return;
            }
            firstVisible.focus();
        }, 125);
    }
    isElementVisible(el) {
        var _a;
        const style = window.getComputedStyle(el);
        const rect = el.getBoundingClientRect();
        const parentRect = (_a = el.parentElement) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        const inSliderView = parentRect ? rect.left < parentRect.right && rect.right > parentRect.left : true;
        const isCheckbox = el.className.includes("checkbox");
        if (isCheckbox)
            return true;
        return style.display !== "none" && style.visibility !== "hidden" &&
            rect.width > 0 && rect.height > 0 && inSliderView;
    }
    displayCurrentStepNumber() {
        if (!this.isFullScreen)
            return;
        const stepNumberElement = DOMHelper.querySelector(OnboardingConstants.activeStepNumberSelector);
        if (stepNumberElement) {
            stepNumberElement.textContent = `${this.currentStepIndex + 1}`;
        }
    }
}
